<script lang="ts">
  import { getConfigurationContext } from "$lib/elev8Api/configurationContext.svelte";
  import { logger } from "$lib/utils/logger";
  import { reportError } from "$lib/errorHandling/errorReporter";
  import { recordTelemetryEvent } from "$lib/telemetry/telemetry";
  import { onDestroy, onMount } from "svelte";
  import { isPreviewMode } from "$lib/utils/previewMode";

  const config = getConfigurationContext();
  const swUrl =
    import.meta.env.MODE === "production" ? "/sw.js" : "/dev-sw.js?dev-sw";
  let registerInterval: ReturnType<typeof setInterval>;

  //TODO only refresh if updating worker, not first visit https://dev.to/thepassle/on-pwa-update-patterns-4fgm
  let isRefreshing: boolean;
  const controllerChangeHandler = () => {
    if (isRefreshing) return;
    logger.debug(
      "[SW-update] Service Worker controller change - new SW has taken control."
    );
    window.location.reload();
    isRefreshing = true;
  };

  onMount(() => {
    if (isPreviewMode()) {
      // A stale SW from the user's normal browsing could otherwise serve
      // cached assets to the preview iframe (Safari/Firefox).
      unregisterServiceWorkersAndReload();
      return;
    }
    if (!shouldRegisterServiceWorker()) return;

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      controllerChangeHandler
    );

    logger.info("[SW-update] Registering service worker...");
    navigator.serviceWorker
      .register(swUrl, {
        type: import.meta.env.MODE === "production" ? "classic" : "module",
        scope: "/",
      })
      .then((registeredSw) => {
        logger.info("[SW-update] Service Worker registered:", registeredSw);

        registerInterval = setInterval(async () => {
          if (registeredSw.installing || registeredSw.waiting) return; // don't check for new SW if one is installing
          if (!registeredSw.active) return; // WebKit transiently nulls active → update() throws
          // Only trust onLine on Chromium, where NetworkInformation backs it.
          // These LAN-connected kiosks report onLine:true with no internet, and
          // WPE's onLine is unreliable — so on WebKit we always attempt rather
          // than risk never updating.
          if ("connection" in navigator && !navigator.onLine) return;
          logger.info("[SW-update] checking for SW updates");
          try {
            await registeredSw.update();
          } catch (e) {
            // WebKit's InvalidStateError is a DOMException that fails
            // `instanceof Error`, so read .name by shape.
            const reason =
              e && typeof e === "object" && typeof (e as Error).name === "string"
                ? (e as Error).name
                : "unknown";
            const failure = {
              name: "frontend_sw_update_failure",
              metricType: "counter" as const,
              value: 1,
              tags: { reason },
            };
            recordTelemetryEvent(failure);
            recordTelemetryEvent({ ...failure, omitDisplayId: true });
          }
        }, config.settings.service_worker_update_interval_ms);
      })
      .catch((e) => {
        reportError("[SW-update] Service Worker registration failed:", e);
      });
  });

  async function unregisterServiceWorkersAndReload() {
    if (!("serviceWorker" in navigator)) return;
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length === 0) return;
      logger.warn(
        `[SW-update] Preview: unregistering ${registrations.length} existing service worker(s) and reloading.`
      );
      const results = await Promise.all(
        registrations.map((r) => r.unregister())
      );
      // Only reload when something was actually removed — guards against a
      // reload loop if unregister() keeps resolving false.
      if (results.some(Boolean)) window.location.reload();
    } catch (e) {
      reportError("[SW-update] Failed to unregister in preview", e);
    }
  }

  onDestroy(() => {
    logger.debug("[SW-update] ServiceWorkerUpdater onDestroy.");
    navigator.serviceWorker?.removeEventListener(
      "controllerchange",
      controllerChangeHandler
    );
    clearInterval(registerInterval);
  });

  function shouldRegisterServiceWorker() {
    let shouldRegister = isSupportedBrowser();

    if (config.settings.service_worker_override === "enable")
      shouldRegister = true;
    if (config.settings.service_worker_override === "disable")
      shouldRegister = false;

    if (!shouldRegister) {
      logger.warn(
        `[SW-update] Did not register service worker. navigator.serviceWorker?: ${
          "serviceWorker" in navigator
        } typeof BigInt: ${typeof BigInt !== "undefined"},
        config.settings.service_worker_override: ${
          config.settings.service_worker_override
        },`
      );
    }
    return shouldRegister;
  }

  function isSupportedBrowser() {
    // Chrome 67+/safari 14+ using BigInt feature detection. https://caniuse.com/?search=bigint
    // prevents Chrome 49 and WPE Webkit (Safari 13.1 ish) from loading SW
    return "serviceWorker" in navigator && typeof BigInt !== "undefined";
  }
</script>
